import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, Type } from '@angular/core';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { DashboardType } from '@app/core/enums/dashboard-types.enum';
import { DashboardHeaderComponent } from '@app/layout/components/dashboard-header/dashboard-header.component';
import { AppointmentListComponent } from '@app/shared/components/appointment-list/appointment-list.component';
import { ContactUsInfoComponent } from '@app/shared/components/contact-us-info/contact-us-info.component';
import { SearchComponent } from '@app/shared/components/search/search.component';
import { Observable, Subject } from 'rxjs';
import {AppointmentSearchParams} from "@app/shared/interfaces/appointmentSearchParams.interface";
import {TokenStorageService} from "@app/core/services/token-storage.service";
import {UserRoles} from "@app/core/enums/userRoles.enum";
import { LoadSpinnerWrapperComponent } from '@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component';
import { NgAppointments } from '@app/shared/interfaces/appointment.interface';
import { PatientSearchListComponent } from '../patient-search-list/patient-search-list.component';

type ComponentInputs = Record<string, unknown>;
type DefaultDashboardTabKey = 'upcoming' | 'previous' | 'search';

export interface GenericDashboardTabConfig {
  key: string;
  label: string;
  component: Type<unknown>;
  inputs?: ComponentInputs;
  containerClass?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  footerComponent?: Type<unknown>;
  footerInputs?: ComponentInputs;
  renderWhenActiveOnly?: boolean;
}

export enum appointmentType {
  FutureAppointments,
  PastAppointments,
  PatientSearch
}

@Component({
  selector: 'app-generic-dashboard',
  imports: [
    DashboardHeaderComponent,
    MatTabsModule,
    NgComponentOutlet,
    NgTemplateOutlet,
    SearchComponent,
    LoadSpinnerWrapperComponent
],
  templateUrl: './generic-dashboard.component.html',
  styleUrl: './generic-dashboard.component.scss'
})
export class GenericDashboardComponent implements OnInit, OnChanges {
  @Input() showHeader = true;
  @Input() useLoadingWrapper = false;
  @Input() loading = false;
  @Input({ required: true }) pageTitle!: string;
  @Input({ required: true }) dashboardType!: DashboardType;

  @Input() appointmentContainerClass = 'appointment-list-container overflow-x-auto w-[99%] mt-4 mb-4 border-2 p-4 ml-auto mr-auto rounded-lg bg-white';
  @Input() appointmentListInputs: ComponentInputs = {};
  @Input() contactInfoInputs: ComponentInputs = {};
  @Input() patientSearchInputs: ComponentInputs = {};
  @Input() customTabConfigs: GenericDashboardTabConfig[] = [];
  @Input() selectedIndex = 0;
  @Input() defaultSearchPlaceholder = 'Search by Patient Name, DOB, Visit, Location, or Provider';
  @Input() fetchAppointments$!: (filter: AppointmentSearchParams) => Observable<NgAppointments>;

  @Output() selectedIndexChange = new EventEmitter<number>();

  tabConfigs: GenericDashboardTabConfig[] = [];

  readonly appointmentListComponent = AppointmentListComponent;
  readonly patientSearchComponent = PatientSearchListComponent;
  readonly contactInfoComponent = ContactUsInfoComponent;

  readonly wcSearchPlaceholder = 'Search by Claim #, Patient Name, or DOB';

  private readonly searchTerms: Record<DefaultDashboardTabKey, string> = {
    upcoming: '',
    previous: '',
    search: ''
  };

  private ngDestroy$ = new Subject<void>();

  constructor(private tokenStorageService: TokenStorageService) {
  }
  ngOnInit(): void {
    this.syncTabConfigs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['customTabConfigs'] ||
      changes['dashboardType'] ||
      changes['appointmentListInputs'] ||
      changes['patientSearchInputs'] ||
      changes['contactInfoInputs']
    ) {
      this.syncTabConfigs();
    }
  }

  trackTab(index: number, tab: GenericDashboardTabConfig): string {
    return tab.key;
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.selectedIndex = event.index;
    this.selectedIndexChange.emit(event.index);
  }

  onTabSearch(tabKey: string, searchTerm: string): void {
    if (!this.isDefaultTabKey(tabKey)) {
      return;
    }
    const normalizedSearchTerm = this.normalizeSearchTerm(searchTerm);

    this.searchTerms[tabKey] = normalizedSearchTerm;
    this.syncTabConfigs();
  }

  // This dashboard used to handle populating appointments and passed the data source to the list/search component.
  // Due to pagination, the loading the data sources were moved to the child components. We may want to move pagination to
  // this component in the future to be able to load data sources here.
  shouldRenderTabContent(tab: GenericDashboardTabConfig, tabIndex: number): boolean {
    return !tab.renderWhenActiveOnly || this.selectedIndex === tabIndex;
  }

  private syncTabConfigs(): void {
    if (this.customTabConfigs.length > 0) {
      this.tabConfigs = this.customTabConfigs;
      return;
    }

    const upcomingAppointmentInputs = {
      ...this.appointmentListInputs,
      title: 'Upcoming Appointments',
      dashboardType: this.dashboardType,
      appointmentListType: appointmentType.FutureAppointments,
      defaultSortField: 'appointmentDate',
      defaultSortDirection: 'asc',
      fetchAppointments$: this.fetchAppointments$,
      search: this.searchTerms['upcoming']
    };

    const previousAppointmentInputs = {
      ...this.appointmentListInputs,
      title: 'Previous Appointments',
      dashboardType: this.dashboardType,
      showStatus: true,
      appointmentListType: appointmentType.PastAppointments,
      fetchAppointments$: this.fetchAppointments$,
      defaultSortField: 'appointmentDate',
      defaultSortDirection: 'desc',
      search: this.searchTerms['previous']
    };

    const patientSearchTabInputs = {
      ...this.patientSearchInputs,
      title: 'Patient Search',
      dashboardType: this.dashboardType,
      showStatus: true,
      appointmentListType: appointmentType.PatientSearch,
      fetchAppointments$: this.fetchAppointments$,
      showReferringProvider: true,
      search: this.searchTerms['search']
    };
    const wcSearchTabInputs = {
      title: 'Patient Search',
      search: this.searchTerms['search']
    };


    this.tabConfigs = [
      {
        key: 'upcoming',
        label: 'Upcoming Appointments',
        component: this.appointmentListComponent,
        inputs: upcomingAppointmentInputs,
        showSearch: true,
        searchPlaceholder: this.defaultSearchPlaceholder,
        containerClass: this.appointmentContainerClass,
        footerComponent: this.contactInfoComponent,
        footerInputs: this.contactInfoInputs
      },
      {
        key: 'previous',
        label: 'Previous Appointments',
        component: this.appointmentListComponent,
        inputs: previousAppointmentInputs,
        showSearch: true,
        searchPlaceholder: this.defaultSearchPlaceholder,
        containerClass: this.appointmentContainerClass,
        footerComponent: this.contactInfoComponent,
        footerInputs: this.contactInfoInputs
      },

    ];

    // We probably need a better way to determine component rendering, but this works fine.
    // As of now, the patient search on referral is not really different than the other tabs,
    // but work comp search has both different fields in the table and different code paths
    // (Searching by WC claim requires multiple other calls and case info is stored per appointment)
    // For the details endpoints, it may make sense to move those to the same endpoints/models and then
    // groupby claim number, instead of the current format of grouping at the API level. However, since
    // referral also uses details and doesn't have claim number/etc we will have to think of the best format
    // to not have redundant code but also don't couple things too strongly.
    if (this.tokenStorageService.getEffectiveUser()?.role === UserRoles.workCompUser){
      this.tabConfigs.push({
        key: 'search',
        label: 'Patient Search',
        component: this.patientSearchComponent,
        inputs: wcSearchTabInputs,
        showSearch: true,
        searchPlaceholder: this.wcSearchPlaceholder,
        footerComponent: this.contactInfoComponent,
        footerInputs: this.contactInfoInputs
      });
    } else {
      this.tabConfigs.push({
        key: 'search',
        label: 'Patient Search',
        component: this.appointmentListComponent,
        inputs: patientSearchTabInputs,
        showSearch: true,
        searchPlaceholder: this.defaultSearchPlaceholder,
        containerClass: this.appointmentContainerClass,
        footerComponent: this.contactInfoComponent,
        footerInputs: this.contactInfoInputs
      })
    }
  }

  private isDefaultTabKey(tabKey: string): tabKey is DefaultDashboardTabKey {
    return tabKey === 'upcoming' || tabKey === 'previous' || tabKey === 'search';
  }

  private normalizeSearchTerm(searchTerm: string): string {
    return (searchTerm ?? '').trim().replace(/\s+/g, ' ');
  }
}
